import { inject, Injectable, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExternalService } from '@finra/angular-data-collection-sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { InstanceService } from './instance.service';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { DisplayRulesService, FilingStatusDisplayRules } from './display-rules.service';
import { GlobalContext } from './global-context.service';
import { OverlayService } from '@finra/forms';
import { AbstractAppBaseEnv, AbstractCompletenessCheckService, NavigationData } from '@finra/rampdi-forms-common';

@Injectable({
  providedIn: 'root',
})
export class CompletenessCheckService extends AbstractCompletenessCheckService {
  initialCheckValue: boolean = false;
  http = inject(HttpClient);
  private _DRS = inject(DisplayRulesService);
  displayRules$: Observable<FilingStatusDisplayRules> = this._DRS.displayRules$;
  globalContext = inject(GlobalContext);
  private window = window;
  openConcurrencyModal$ = new BehaviorSubject(false);

  // Override: rampdi-forms-common calls validateUsingPOST which was renamed to validate in SDK 2.20.0
  //override validateInstance(instanceData: any): Observable<{ success: boolean; data: any }> {
  //const instance = deepClone(instanceData);
  //instance.instanceMetadata.status = 'SUBMITTED';
  //instance.instanceId = instance.id;
  //return (this as any)._es
  //.validate(instance.templateNamespace, instance.templateName, instance.templateVersion, instance)
  //.pipe(
  //map((data) => ({ success: true, data })),
  //catchError((err) => of({ success: false, data: err })),
  //);
  //}
  disclosureDeleteModal?: TemplateRef<any>;
  setDisclosureDeleteModal(template: TemplateRef<any>) {
    this.disclosureDeleteModal = template;
  }
  setAppComponent(component: any) {
    this.appComponent = component;
  }
  setFormSelector(component: any) {
    this.formSelector = component;
  }
  concurrencyDetected: boolean = false;
  appComponent: any;
  formSelector: any;

  constructor(
    private es: ExternalService,
    public route: ActivatedRoute,
    public router: Router,
    private instanceService: InstanceService,
    private OS: OverlayService,
  ) {
    super(es, route);
  }

  /** Internal schema paths that should not be shown as CC errors to users */
  private readonly INTERNAL_POINTER_SUFFIXES = [
    '/expenseInfoMaxId',
    '/expenseInfoItems',
    '/webAddressMaxId',
    '/webAddressItems',
  ];

  /** Mailing address user-entered fields (MU3 model names) used to detect an empty address. */
  private static readonly MAILING_ADDRESS_USER_FIELDS = [
    'street1',
    'street2',
    'city',
    'state',
    'country',
    'postalCode',
  ] as const;

  private static readonly MAILING_ADDRESS_REQUIRED_MESSAGE =
    'The mailing address is required. Street, city, postal code and country must be provided.';

  /** Anchored mailing-address pointer prefix (path-segment match, not loose substring). */
  private static readonly MAILING_ADDRESS_POINTER = '#/identifyingInformation/mailingAddress';

  /** Section bucket where DECAF emits object-level mailing asserts (postal/state) that carry no field pointer. */
  private static readonly IDENTIFYING_INFO_POINTER = '#/identifyingInformation';

  /** Substrings that identify a mailing-address message inside the section-level bucket. */
  private static readonly MAILING_ADDRESS_MESSAGE_SUBSTRINGS = ['for the mailing address', 'mailing address'];

  override classifyErrorList(
    errorList?: { [pointer: string]: string[] },
    warningList?: { [pointer: string]: string[] },
  ) {
    const filteredErrors = this.collapseMailingAddressRequired(this.filterInternalPointers(errorList));
    const filteredWarnings = this.filterInternalPointers(warningList);
    return super.classifyErrorList(filteredErrors, filteredWarnings);
  }

  private filterInternalPointers(list?: { [pointer: string]: string[] }): { [pointer: string]: string[] } | undefined {
    if (!list) return list;
    const filtered: { [pointer: string]: string[] } = {};
    for (const pointer of Object.keys(list)) {
      const isInternal = this.INTERNAL_POINTER_SUFFIXES.some((suffix) => pointer.endsWith(suffix));
      if (!isInternal) {
        filtered[pointer] = list[pointer];
      }
    }
    return filtered;
  }

  /** True when the user has entered no part of the mailing address. */
  private isMailingAddressEmpty(): boolean {
    const mailingAddress = this.instanceService.currentInstance?.collectedData?.identifyingInformation?.mailingAddress;
    if (!mailingAddress) return true;
    return CompletenessCheckService.MAILING_ADDRESS_USER_FIELDS.every((field) => {
      const value = mailingAddress[field];
      return value === undefined || value === null || String(value).trim() === '';
    });
  }

  // Collapse mailing errors (field-level pointers + section-level messages) into one required check when empty; never sweeps main-address/other errors (NMLSMOD-20983, MU1/MU4 parity).
  private collapseMailingAddressRequired(list?: {
    [pointer: string]: string[];
  }): { [pointer: string]: string[] } | undefined {
    if (!list || !this.isMailingAddressEmpty()) return list;

    const MAILING_PTR = CompletenessCheckService.MAILING_ADDRESS_POINTER;
    const SECTION_PTR = CompletenessCheckService.IDENTIFYING_INFO_POINTER;
    const isMailingMessage = (msg: string): boolean =>
      CompletenessCheckService.MAILING_ADDRESS_MESSAGE_SUBSTRINGS.some((s) => msg.toLowerCase().includes(s));

    const hasFieldLevelMailing = Object.keys(list).some((p) => p.startsWith(`${MAILING_PTR}/`) || p === MAILING_PTR);
    const sectionMessages = list[SECTION_PTR] || [];
    const hasSectionMailing = sectionMessages.some(isMailingMessage);
    if (!hasFieldLevelMailing && !hasSectionMailing) return list;

    const collapsed: { [pointer: string]: string[] } = {};
    for (const pointer of Object.keys(list)) {
      if (pointer === MAILING_PTR || pointer.startsWith(`${MAILING_PTR}/`)) {
        // Drop field-level mailing pointers; the single required check is added once below.
        continue;
      }
      if (pointer === SECTION_PTR) {
        // Keep non-mailing section messages; mailing ones are folded into the required check.
        const nonMailing = sectionMessages.filter((msg) => !isMailingMessage(msg));
        if (nonMailing.length) collapsed[SECTION_PTR] = nonMailing;
        continue;
      }
      collapsed[pointer] = list[pointer];
    }
    // Single consolidated required check at the stable anchored pointer.
    collapsed[MAILING_PTR] = [CompletenessCheckService.MAILING_ADDRESS_REQUIRED_MESSAGE];
    return collapsed;
  }

  preprocessNavData(navData: NavigationData[]) {
    return navData;
  }

  private get environment(): AbstractAppBaseEnv {
    return environment;
  }

  refreshFormData() {
    // Force the form to refresh by updating the instance service and triggering change detection
    this.instanceService.currentInstance = { ...this.instanceService.currentInstance };
  }

  gotoField(selectors: string[]) {
    setTimeout(async () => {
      try {
        console.log('selectors', selectors);
        if (selectors.length > 1) {
          const arraySelector: any = selectors.shift();
          const formControlIndex = +selectors.shift();
          const formControlKey = arraySelector.replaceAll('-', '.');
          //Since disclosures explanations can have multiple associations, we need to only trigger one event, otherwise multiple modals are opened.
          if (formControlKey?.includes('disclosureExplanations')) {
            if (selectors?.includes('associations')) {
              this.appComponent.selectedDisclosureIndex = null;
              this.appComponent.currentSelectDisclosure = {
                associations: [],
              };
              const isDisabled = new BehaviorSubject<boolean>(true);
              let componentModel =
                this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                  ?.disclosureExplanations || [];
              let prevComponentModel = deepClone(
                this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                  ?.disclosureExplanations || [],
              );

              this.appComponent.modalDeletionReason = '';
              const specialChars = /^((?!.*<(?! *>).*>)(?! +$|\r?\n+$).+|\r?\n)*$/;
              const initialDeleteReason =
                componentModel[this.appComponent.selectedDisclosureIndex]?.disclosureExplanationDeleteReason;
              if (
                !initialDeleteReason ||
                initialDeleteReason.trim().length === 0 ||
                specialChars.test(initialDeleteReason)
              ) {
                isDisabled.next(true);
              } else {
                isDisabled.next(false);
              }
              let validationError$ = new BehaviorSubject<boolean>(false);
              const validateDeleteReason = (event) => {
                const validateInput = (value: string) => {
                  return value && value.trim().length > 0 && specialChars.test(value);
                };
                const value = event?.target?.value;
                this.appComponent.modalDeletionReason = value;

                const selectedItem = componentModel[this.appComponent.selectedDisclosureIndex];
                const isNewStatus = selectedItem?.disclosureExplanationStatus === 'CREATE';

                if (!value || !validateInput(value) || !selectedItem) {
                  if (!value || value.trim().length === 0) {
                    this.appComponent.disclosureDeleteReasonError = 'A reason for deleting an explanation is required.';
                    validationError$.next(true);
                  } else if (!specialChars.test(value)) {
                    this.appComponent.disclosureDeleteReasonError =
                      'HTML content is not allowed. Please remove any HTML-formatted text (e.g., <p>).';
                    validationError$.next(true);
                  } else {
                    validationError$.next(false);
                  }
                  isDisabled.next(!isNewStatus);
                  return;
                } else {
                  selectedItem.disclosureExplanationDeleteReason = value;
                  isDisabled.next(false);
                  validationError$.next(false);
                  this.appComponent.disclosureDeleteReasonError = '';
                }
              };

              try {
                await this.ensureEnumOptions();
              } catch (error) {
                console.error('Failed to ensure enum options:', error);
              }
              this.OS.open(this.disclosureDeleteModal, {
                theme: 'info',
                title: 'Delete Existing Explanation',
                buttonJustifyContent: 'end',
                templateData: {
                  model: componentModel,
                  isDisabled,
                  validateDeleteReason: validateDeleteReason,
                  validationError: validationError$,
                },
                buttons: [
                  { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel explanation deletion' },
                  {
                    name: 'Delete Selected Explanation',
                    value: true,
                    theme: 'primary',
                    isDisabled: isDisabled.asObservable(),
                    'aria-label': 'Delete selected explanation',
                  },
                ],
              })
                .afterClosed$.toPromise()
                .then((e) => {
                  if (e.data) {
                    const selectedItem = componentModel[this.appComponent.selectedDisclosureIndex];
                    if (selectedItem?.disclosureExplanationStatus === 'CREATE') {
                      componentModel.splice(this.appComponent.selectedDisclosureIndex, 1);
                    } else {
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationStatus = 'REMOVE';
                      componentModel[this.appComponent.selectedDisclosureIndex].updatedDate = new Date().toISOString();
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationAmendmentReason =
                        null;
                    }
                    // Refresh form data to reflect the updated instance
                    this.refreshFormData();
                  } else {
                    if (this.appComponent?.selectedDisclosureIndex) {
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationDeleteReason =
                        prevComponentModel?.[
                          this.appComponent.selectedDisclosureIndex
                        ]?.disclosureExplanationDeleteReason;
                    }
                  }
                });
              return;
            }

            const model =
              this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                ?.disclosureExplanations;
            const targetItem = model?.[formControlIndex];
            let modalOpened = false;

            if (targetItem && model) {
              const questionTables = Array.from(
                document.querySelectorAll(`formly-field.explanation-assoc fin-array-type`),
              );
              for (const el of questionTables) {
                if (modalOpened) break;
                const rows = el.shadowRoot?.querySelector('finra-table')?.shadowRoot?.querySelector('tbody')?.children;
                if (rows && rows.length > 0) {
                  const filteredModel = model.filter((exp) => exp.type === 'question');
                  const sortedModel = [...filteredModel].sort(
                    (a, b) => new Date(b.updatedDate || 0).getTime() - new Date(a.updatedDate || 0).getTime(),
                  );
                  const targetRowIndex = sortedModel.findIndex((item) => item === targetItem);

                  if (targetRowIndex >= 0 && rows[targetRowIndex]) {
                    const icon = rows[targetRowIndex].querySelector('finra-icon[title="Edit"]');
                    if (icon) {
                      (icon as HTMLElement).click();
                      modalOpened = true;
                      break;
                    }
                  }
                }
              }

              if (!modalOpened) {
                const categoryTables = Array.from(
                  document.querySelectorAll(`formly-field.category-assoc fin-array-type`),
                );
                for (const el of categoryTables) {
                  if (modalOpened) break;
                  const rows = el.shadowRoot
                    ?.querySelector('finra-table')
                    ?.shadowRoot?.querySelector('tbody')?.children;
                  if (rows && rows.length > 0) {
                    const filteredModel = model.filter((exp) => exp.type === 'category');
                    const sortedModel = [...filteredModel].sort(
                      (a, b) => new Date(b.updatedDate || 0).getTime() - new Date(a.updatedDate || 0).getTime(),
                    );
                    const targetRowIndex = sortedModel.findIndex((item) => item === targetItem);

                    if (targetRowIndex >= 0 && rows[targetRowIndex]) {
                      const icon = rows[targetRowIndex].querySelector('finra-icon[title="Edit"]');
                      if (icon) {
                        (icon as HTMLElement).click();
                        modalOpened = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          } else {
            const isDocument = formControlKey?.includes('documents');
            const isDisclosure = formControlKey?.includes('disclosureQuestions');

            Array.from(document.querySelectorAll(`formly-field.${arraySelector}`)).forEach((formlyField: any) => {
              const el = formlyField.querySelector('fin-array-type');

              if (isDocument || isDisclosure) {
                // Get model from instance service
                const model = isDocument
                  ? this.instanceService.currentInstance?.collectedData?.documents
                  : this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                      ?.disclosureExplanations;

                if (!model) {
                  return;
                }

                const targetItem = model[formControlIndex];

                // Find the table and rows
                const table = el?.shadowRoot?.querySelector('finra-table');
                const tbody = table?.shadowRoot?.querySelector('tbody');
                const rows = tbody?.children;

                // Sort model by updatedDate descending to match table display
                const sortedModel = [...model].sort((a, b) => {
                  const dateA = new Date(a.updatedDate || 0).getTime();
                  const dateB = new Date(b.updatedDate || 0).getTime();
                  return dateB - dateA;
                });

                // Find target item position in sorted array
                const targetRowIndex = sortedModel.indexOf(targetItem);

                if (rows && targetRowIndex >= 0 && rows[targetRowIndex]) {
                  const row = rows[targetRowIndex];
                  const cells = row.querySelectorAll('td');

                  for (let i = 0; i < cells.length; i++) {
                    const icon = cells[i].querySelector('finra-icon[title="Edit"]');
                    if (icon) {
                      (icon as HTMLElement).click();
                      break;
                    }
                  }
                }
              } else {
                // Check if the array has items; if empty, click the "Add" link to open the add modal
                const expenseModel =
                  this.instanceService.currentInstance?.collectedData?.expenseInformation?.expenseInfoItems;
                if (
                  formControlKey === 'expenseInformation.expenseInfoItems' &&
                  (!expenseModel || expenseModel.length === 0)
                ) {
                  const addLink = formlyField.querySelector('.icon-links-container a.icon-button') as HTMLElement;
                  if (addLink) {
                    addLink.click();
                  } else {
                    formlyField.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                } else {
                  el?.dispatchEvent(
                    new CustomEvent('edit', { detail: { formControlIndex, formControlKey, selectors } }),
                  );
                }
              }
            });
          }
        } else if (selectors[0] === 'others') {
          const globalContextStore = this.globalContext?.store?.();
          const permissions = globalContextStore?.permissions || [];
          if (this._DRS.userPermissions.isCompanyUser && this.concurrencyDetected) {
            this.setCompletenessCheckData({ open: false, loading: false });
            this.openConcurrencyModal$.next(true);
            return;
          }
          if (
            permissions.includes('MU3') ||
            permissions.includes('MU3Submitter') ||
            permissions.includes('Administrator')
          ) {
            const subjectId = this.instanceService.currentInstance.instanceMetadata?.target?.['subjectId'];
            const baseUrl = `${environment.urls.individualSummary}/${subjectId}/`;
            const queryParams = '?activeTab=relationships';
            const fullUrl = `${baseUrl}${queryParams}`;
            window.open(fullUrl, '_self');
          }
        } else {
          const selectorKey = selectors.shift();
          // For hasExpenses errors, scroll to the expense array section
          if (selectorKey === 'operationInformation-hasExpenses') {
            const expenseField = document.querySelector('formly-field.expenseInformation');
            if (expenseField) {
              expenseField.scrollIntoView({ behavior: 'smooth', block: 'start' });
              return;
            }
          }
          const selector = `formly-field.${selectorKey}`;
          const element = document.querySelector(selector);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (selectorKey && selectorKey.toLowerCase().includes('mailingaddress')) {
            // Empty mailing address renders only the Add/Edit Mailing Address button (no input fields),
            // so the formly-field target does not exist; fall back to that button (NMLSMOD-20983 parity).
            const addBtn = document.querySelector(
              'finra-button[aria-label="Add Mailing Address"], finra-button[aria-label="Edit Mailing Address"], [aria-label="Add Mailing Address"], [aria-label="Edit Mailing Address"]',
            ) as HTMLElement | null;
            if (addBtn) {
              addBtn.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setTimeout(() => addBtn.focus(), 600);
            }
          }
        }
      } catch (error) {
        console.error('Error in gotoField:', error);
      }
    });
  }

  getSelectors(item: { navData: NavigationData; pointer: string }, section) {
    if (section.id === 'others') {
      item.navData = { id: 'others' };
    }
    const { modelPath = [item.navData.id] } = deepClone(item?.navData);
    let skipModelPath = false;
    const classNameList = [];
    let className = [];
    const pathSegments = item.pointer.split('/').slice(1);
    for (const prop of pathSegments) {
      if (!skipModelPath && prop === modelPath.shift()) {
        continue;
      } else {
        skipModelPath = true;
        if (this.isArrayIndex(prop)) {
          classNameList.push(className.join('-'), prop);
          className = [];
        } else {
          className.push(prop);
        }
      }
    }
    if (className.length) {
      classNameList.push(className.join('-'));
    }
    if (section.id === 'others') {
      classNameList.push('others');
    }
    return classNameList;
  }

  onValidateCCOpened() {
    this.setCompletenessCheckData({ open: true, loading: true });

    // Re-fetch profile data to ensure attestation page has fresh data
    this.globalContext.getModProfile().catch((e) => console.warn('[CC] getModProfile refresh failed:', e));

    // Need to deep clone the currentInstance data to avoid CC error on cloudwell flow
    const instanceData = deepClone(this.instanceService?.currentInstance);
    if (instanceData?.collectedData?.cloudwell) {
      delete instanceData.collectedData.cloudwell;
    }

    this.validateInstance(instanceData).subscribe((ccResult) => {
      if (ccResult.success) {
        this.completenessCheckOthersMessage(ccResult);
      } else if (ccResult?.data?.status === 400) {
        this.completenessCheckOthersMessage(ccResult);
      } else if (ccResult?.data?.status === 403) {
        this.setCompletenessCheckData({ open: false, loading: false });
        if (sessionStorage.getItem('cc403Reload')) {
          sessionStorage.removeItem('cc403Reload');
          return;
        }
        globalThis.finformUnsavedChanges = false;
        sessionStorage.setItem('cc403Reload', 'true');
        this.window.location.reload();
      } else {
        this.setCompletenessCheckData({ open: true, fail: true });
      }
    });
  }

  checkConcurrencyDetected(res) {
    Object.keys(res?.data?.error?.errorDetails || {}).forEach((key) => {
      const value = res?.data?.error?.errorDetails[key];
      if (value[0].includes('Updates to your MU3 have been submitted since creating this draft.')) {
        this.concurrencyDetected = true;
      }
    });
  }

  completenessCheckOthersMessage(res) {
    // Forcing Concurrency issue to be resolved first
    this.checkConcurrencyDetected(res);
    if (this.concurrencyDetected) {
      this.setCompletenessCheckData(
        this.classifyErrorList(res?.data?.error?.errorDetails, res?.data?.error?.warningDetails),
      );
      return;
    }

    // Process CC result
    this.processCompletenessCheckResult(res);
  }

  private processCompletenessCheckResult(res) {
    const hasErrors = Object.keys(res?.data?.error?.errorDetails || {}).length > 0;
    const hasWarnings = Object.keys(res?.data?.error?.warningDetails || {}).length > 0;

    if (hasErrors || hasWarnings) {
      this.setCompletenessCheckData({
        ...this.classifyErrorList(res?.data?.error?.errorDetails, res?.data?.error?.warningDetails),
        fail: false,
        loading: false,
      });
    } else if (res.success) {
      this.setCompletenessCheckData({
        open: true,
        success: true,
        count: { error: 0, warning: 0, total: 0 },
        lastUpdated: Date.now(),
        successMessage: `${this.environment.formName} completeness check passed successfully`,
        loading: false,
      });
    }
  }

  private async ensureEnumOptions() {
    if (this.formSelector?.disclosureExplanationStatusOptions?.length) return;

    // Try to get from already built field
    const field = this.formSelector?.form?.get('collectedData.disclosureQuestions.disclosures.disclosureExplanations');
    const columns = field?.options?.fieldArray?.fieldGroup?.[0]?.props?.columns;

    if (columns) {
      columns.forEach((col) => {
        if (col.key === 'disclosureExplanationStatus' && col.props?.options) {
          this.formSelector.disclosureExplanationStatusOptions = [...col.props.options];
        }
        if (col.key === 'dispositionType' && col.props?.options) {
          this.formSelector.dispositionTypeOptions = [...col.props.options];
        }
      });
    }

    // If still not available, fetch from API
    if (!this.formSelector?.disclosureExplanationStatusOptions?.length) {
      try {
        const response = await this.http
          .get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=DISC_EXPL_STATUS&activeData=true`,
          )
          .toPromise();
        const oneOf = response?.oneOf || [];
        this.formSelector.disclosureExplanationStatusOptions = oneOf.map((item) => ({
          value: item.const,
          label: item.title,
        }));
      } catch (error) {
        console.error('Failed to load disclosure status options:', error);
      }
    }

    if (!this.formSelector?.dispositionTypeOptions?.length) {
      try {
        const response = await this.http
          .get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=DISC_DISP_TYPES&activeData=false`,
          )
          .toPromise();
        const oneOf = response?.oneOf || [];
        this.formSelector.dispositionTypeOptions = oneOf.map((item) => ({ value: item.const, label: item.title }));
      } catch (error) {
        console.error('Failed to load disposition type options:', error);
      }
    }
  }
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}
